import React, { useMemo, useState } from 'react';
import { useChats } from '../../contexts/ChatsContext';
import { useProxyData } from '../../contexts/ProxyDataContext';
import { useEmailInbox } from '../../contexts/EmailInboxContext';
import { proxyNowStamp } from '../../data/mockProxyJobs';
import { type ProxyInboxEmail } from '../../data/mockProxyEmails';
import {
  PROXY_WORKFLOW_REGISTRY, PROXY_SOURCE_LABEL, ticketKey,
  type ProxyTask, type ProxyTaskDetailRow, type ProxyExtractedField, type ProxyCalcRow,
} from '../../data/proxyWorkflowRegistry';
import { resolveProxyJob } from '../../utils/proxyQueue';
import { useProxyEscalation } from '../../hooks/useProxyEscalation';
import { useTeamMessages } from '../../contexts/TeamMessagesContext';
import { getMaterialReceipts, updateMaterialReceipt } from '../../services/materialReceiptService';
import { noteStamp } from '../../data/receiptDraft';
import { EscalateControl } from './EscalateControl';
import { QuickCheck } from '../feedback/QuickCheck';

// "an inbound email" / "a job signal" / "a Fulfillment escalation" for the
// header's intro sentence — same source enum as PROXY_SOURCE_LABEL, just
// lower-case with its own article instead of a Title Case chip label.
const SOURCE_INTRO: Record<ProxyTask['source'], string> = {
  email: 'an inbound email',
  job_signal: 'a job signal',
  fulfillment_escalation: 'a Fulfillment escalation',
};

interface Props {
  task: ProxyTask;
  sessionId: string;
  // Reopening a frozen card from chat history — display only, no writeback.
  // `approvedOverride` reflects the snapshot's own approval state rather than
  // the (possibly since-superseded) live session state.
  readOnly?: boolean;
  approvedOverride?: boolean;
}

const GRAD = 'linear-gradient(135deg,#14b8a6,#0891b2)';

// ── Fixed shell — styled with the exact BetaNXT tokens from the item-panel
// mock (ace_item_panel_mock.html): #043243/#595A60/#73747A/#E5E7EB/#063F5A/
// #E9F6F6, a distinct token family from the ink/copy/muted/trim/wash aliases
// the chat-panel components use elsewhere, so these are literal Tailwind
// arbitrary values to match the mock exactly rather than approximated
// through those aliases. Every workflow renders through this same shell —
// only the schema-driven sections below (extracted/calculation/drafts)
// change per workflow. ──────────────────────────────────────────────────

type ChipVariant = 'type' | 'src' | 'conf' | 'amber';

const CHIP_CLASS: Record<ChipVariant, string> = {
  type: 'bg-[#E9F6F6] text-[#063F5A]',
  src: 'bg-[#EEF4FF] text-[#2563EB]',
  conf: 'bg-[#E9F6F6] text-[#0F6E56]',
  amber: 'bg-[rgba(250,204,21,.12)] border border-[rgba(250,204,21,.6)] text-[#7a5b00]',
};

function Chip({ variant, children }: { variant: ChipVariant; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs leading-4 px-2 py-[3px] rounded-md font-medium ${CHIP_CLASS[variant]}`}>
      {children}
    </span>
  );
}

function StepDot({ state, n }: { state: 'done' | 'active'; n: number }) {
  return (
    <span className={`w-[22px] h-[22px] rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
      state === 'done' ? 'bg-[#1D9E75] text-white' : 'bg-[#063F5A] text-white'
    }`}>
      {state === 'done' ? (
        <svg width="11" height="11" viewBox="0 0 14 14" fill="none"><path d="M2 7l3.5 3.5L12 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      ) : n}
    </span>
  );
}

function Stepper({ steps, draftApproved }: { steps: [string, string, string]; draftApproved: boolean }) {
  const states: { label: string; state: 'done' | 'active' }[] = [
    { label: steps[0], state: 'done' },
    { label: steps[1], state: 'done' },
    { label: steps[2], state: draftApproved ? 'done' : 'active' },
  ];
  return (
    <div data-tour="task-stepper" className="flex items-center gap-2 px-6 py-4 border-b border-[#E5E7EB] bg-white">
      {states.map((s, i) => (
        <React.Fragment key={s.label}>
          <div className={`flex items-center gap-2 text-[13px] whitespace-nowrap ${s.state === 'active' ? 'text-[#063F5A] font-semibold' : 'text-[#043243]'}`}>
            <StepDot state={s.state} n={i + 1} />
            {s.label}
          </div>
          {i < states.length - 1 && <div className="flex-1 h-[1.5px] bg-[#E5E7EB] min-w-[24px]" />}
        </React.Fragment>
      ))}
    </div>
  );
}

function Section({ index, title, trailing, tourId, children }: { index: number; title: string; trailing?: React.ReactNode; tourId: string; children: React.ReactNode }) {
  return (
    <div data-tour={`task-${tourId}`} className="py-[18px] border-b border-[#E5E7EB] last:border-b-0">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[.4px] text-[#595A60] font-semibold mb-3">
        {index} · {title} {trailing}
      </div>
      {children}
    </div>
  );
}

function GridCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="bg-white px-3.5 py-2.5">
      <div className="text-xs text-[#73747A] uppercase tracking-[.3px]">{label}</div>
      <div className="text-sm font-semibold text-[#043243] mt-[3px]">{children}</div>
    </div>
  );
}

function PillToggle({ options, value, onChange }: { options: { value: any; label: string }[]; value: any; onChange: (v: any) => void }) {
  return (
    <div className="flex gap-1.5">
      {options.map(o => (
        <button
          key={o.label}
          onClick={() => onChange(o.value)}
          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
            value === o.value ? 'bg-[#063F5A] text-white' : 'bg-white border border-[#E5E7EB] text-[#043243]'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Renders any workflow's extracted-field schema generically — a field is
// read-only text unless it declares `editable`, in which case its kind
// (enum → pill toggle, number → number input) decides the control.
function ExtractedFieldCell({ field, editing, onChange }: { field: ProxyExtractedField; editing: boolean; onChange: (value: any) => void }) {
  if (field.strike) {
    return <GridCell label={field.label}><span className="text-[#73747A] line-through">{field.display}</span></GridCell>;
  }
  if (editing && field.editable?.kind === 'enum') {
    return <GridCell label={field.label}><PillToggle options={field.editable.options} value={field.editable.value} onChange={onChange} /></GridCell>;
  }
  if (editing && field.editable?.kind === 'number') {
    return (
      <GridCell label={field.label}>
        <input
          type="number" min={0} value={field.editable.value}
          onChange={e => onChange(Math.max(0, parseInt(e.target.value, 10) || 0))}
          className="w-full px-2 py-1 rounded-md border border-[#E5E7EB] text-sm text-[#043243] outline-none focus:border-[#1D9E75]"
        />
      </GridCell>
    );
  }
  return <GridCell label={field.label}>{field.display}</GridCell>;
}

// `value` is the person's override when they've changed Ace's figure, else
// Ace's own row.value.
function CalcRowView({ row, value, emphasize, editing, onChange }: {
  row: ProxyCalcRow; value: string; emphasize?: boolean; editing: boolean; onChange: (value: string) => void;
}) {
  const toneClass = row.tone === 'positive' ? 'text-[#0F6E56] font-semibold'
    : row.tone === 'muted' ? 'text-[#595A60]'
    : row.tone === 'amber' ? 'text-[#7a5b00] font-semibold'
    : '';
  return (
    <div className={`${emphasize ? 'flex justify-between border-t border-[#E5E7EB] mt-1.5 pt-2.5 text-[15px] font-bold text-[#063F5A]' : 'flex justify-between text-[13px]'} gap-3 items-center`}>
      <span className={emphasize ? '' : 'text-[#043243]'}>{row.label}</span>
      {editing ? (
        <input
          type="text" value={value}
          onChange={e => onChange(e.target.value)}
          className="w-1/2 text-right text-[13px] font-normal text-[#043243] bg-[#FAFBFC] border border-dashed border-[#E5E7EB] rounded-md px-2 py-1 outline-none focus:border-[#1D9E75]"
        />
      ) : (
        <span className={`text-right ${emphasize ? '' : toneClass}`}>{value}</span>
      )}
    </div>
  );
}

function DraftCard({ icon, label, to, cc, note, subject, body, editing, onToggleEdit, onChange }: {
  icon: string; label: string; to: string; cc?: string[]; note?: string; subject?: string; body: string;
  editing: boolean; onToggleEdit: () => void;
  onChange: (patch: { to?: string; cc?: string[]; subject?: string; body?: string }) => void;
}) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg px-4 py-3.5 mb-3 last:mb-0">
      <div className="flex justify-between items-center mb-2">
        <span className="font-semibold text-[13px] text-[#043243]">{icon} {label}</span>
        <button onClick={onToggleEdit} className="text-xs text-[#2563EB] font-medium">{editing ? 'Done' : 'Edit'}</button>
      </div>
      {editing ? (
        <div className="flex flex-col gap-1.5 mb-2">
          <label className="flex items-center gap-1.5 text-xs text-[#595A60]">
            To
            <input
              type="text" value={to} placeholder="recipient email"
              onChange={e => onChange({ to: e.target.value })}
              className="flex-1 text-xs text-[#043243] bg-[#FAFBFC] border border-dashed border-[#E5E7EB] rounded-md px-2 py-1 outline-none focus:border-[#1D9E75]"
            />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-[#595A60]">
            Cc
            <input
              type="text" value={(cc ?? []).join(', ')} placeholder="add recipients, comma-separated"
              onChange={e => onChange({ cc: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })}
              className="flex-1 text-xs text-[#043243] bg-[#FAFBFC] border border-dashed border-[#E5E7EB] rounded-md px-2 py-1 outline-none focus:border-[#1D9E75]"
            />
          </label>
          {subject !== undefined && (
            <label className="flex items-center gap-1.5 text-xs text-[#595A60]">
              Subject
              <input
                type="text" value={subject}
                onChange={e => onChange({ subject: e.target.value })}
                className="flex-1 text-xs text-[#043243] bg-[#FAFBFC] border border-dashed border-[#E5E7EB] rounded-md px-2 py-1 outline-none focus:border-[#1D9E75]"
              />
            </label>
          )}
          {note && <div className="text-xs text-[#73747A]">{note}</div>}
        </div>
      ) : (
        <div className="text-xs text-[#595A60] mb-2">
          To: {to || <span className="text-[#7a5b00] font-medium">add a recipient (click Edit)</span>}{cc && cc.length > 0 && ` · cc: ${cc.join(', ')}`}{note && ` · ${note}`}
        </div>
      )}
      {editing ? (
        <textarea
          value={body}
          onChange={e => onChange({ body: e.target.value })}
          rows={5}
          className="w-full text-[13px] text-[#043243] bg-[#FAFBFC] border border-dashed border-[#E5E7EB] rounded-md px-3 py-2.5 outline-none focus:border-[#1D9E75] resize-none font-[inherit]"
        />
      ) : (
        <div className="text-[13px] text-[#043243] bg-[#FAFBFC] border border-dashed border-[#E5E7EB] rounded-md px-3 py-2.5 whitespace-pre-line">
          {subject ? `Subject: ${subject}\n\n${body}` : body}
        </div>
      )}
    </div>
  );
}

// Source detail cards, nested inside the section-1 toggle — generic across
// every workflow (email vs job-signal is a ProxyTask-level concept, not a
// per-workflow one).
function EmailCard({ email }: { email: ProxyInboxEmail }) {
  return (
    <div className="px-4 py-3.5">
      <div className="text-[13px] text-[#595A60] mb-0.5"><b className="text-[#043243] font-semibold">From:</b> {email.from} &lt;{email.fromEmail}&gt;</div>
      <div className="text-[13px] text-[#595A60] mb-0.5"><b className="text-[#043243] font-semibold">To:</b> {email.to}</div>
      <div className="font-semibold text-[#043243] my-1.5">{email.subject}</div>
      <div className="text-[#595A60] text-[13px]">"{email.body}"</div>
    </div>
  );
}

// Generic label/value detail card for any non-email source (job signal,
// Fulfillment escalation, and whatever a future workflow's trigger needs) —
// each workflow builds its own `sourceDetail.rows` at task-build time, so
// this component never needs to know what a "job signal" or "escalation" is.
function DetailCard({ rows }: { rows: ProxyTaskDetailRow[] }) {
  return (
    <div className="px-4 py-3.5">
      {rows.map(r => (
        <div key={r.label} className="text-[13px] text-[#595A60] mb-0.5 last:mb-0">
          <b className="text-[#043243] font-semibold">{r.label}:</b> {r.value}
        </div>
      ))}
    </div>
  );
}

export function ProxyTaskPanel({ task, sessionId, readOnly = false, approvedOverride }: Props) {
  const { sessions, appendMsg, updateSession } = useChats();
  const { jobs, appendFollowUp, updateJob, recordApproval } = useProxyData();
  const { escalate } = useProxyEscalation();
  const { send: sendTeamMessage } = useTeamMessages();
  const { proxyEmails, appendOutbound } = useEmailInbox();
  const session = sessions.find(s => s.id === sessionId);
  const job = jobs.find(j => j.jobId === task.jobId);
  const workflow = PROXY_WORKFLOW_REGISTRY[task.workflowType];

  const email = task.source === 'email' ? proxyEmails.find(e => e.id === task.sourceEmailId) : undefined;
  const isHighConfidence = task.confidence >= 90;

  const [srcOpen, setSrcOpen] = useState(!isHighConfidence);
  const [editingFields, setEditingFields] = useState(false);
  const [approved, setApproved] = useState(session?.proxyTaskApproved === true);
  const displayApproved = readOnly ? !!approvedOverride : approved;
  const [edits, setEdits] = useState<Record<string, any>>(() => job ? workflow.defaultEdits(task, job) : {});
  const [draftEditing, setDraftEditing] = useState<Record<string, boolean>>({});
  const [draftOverrides, setDraftOverrides] = useState<Record<string, { to?: string; cc?: string[]; subject?: string; body?: string }>>({});
  // Section 3 corrections, keyed by row label, for when Ace got a figure wrong.
  const [calcEditing, setCalcEditing] = useState(false);
  const [calcOverrides, setCalcOverrides] = useState<Record<string, string>>({});

  const groupJobs = useMemo(() => (task.jobIds || []).map(id => jobs.find(j => j.jobId === id)).filter((j): j is NonNullable<typeof j> => !!j), [task.jobIds, jobs]);
  const schema = useMemo(() => job ? workflow.buildPanel({ task, job, edits, email, groupJobs }) : null, [workflow, task, job, edits, email, groupJobs]);

  if (!job || !schema) {
    return <div className="p-5 text-sm text-[#595A60]">Job {task.jobId} could not be found.</div>;
  }

  const band = resolveProxyJob(job, null).band;
  const canEditFields = schema.extractedFields.some(f => !!f.editable);
  const calcRows = [...schema.calculation.rows, schema.calculation.totalRow, ...(schema.calculation.deltaRow ? [schema.calculation.deltaRow] : [])];
  const calcValue = (row: ProxyCalcRow) => calcOverrides[row.label] ?? row.value;
  const calcChanged = calcRows.some(r => calcValue(r) !== r.value);
  const setCalcValue = (row: ProxyCalcRow) => (value: string) => setCalcOverrides(prev => ({ ...prev, [row.label]: value }));
  // Ace can't know who an email should go to, so the person always adds it.
  const missingRecipient = schema.drafts.some(d => !(draftOverrides[d.key]?.to ?? '').trim());

  function now() {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // Fulfillment's Received Short receipt for this job now waits on the new
  // material, which puts "Merge arriving material" on it.
  async function markReceiptAwaitingMaterial(jobId: string) {
    try {
      const receipt = (await getMaterialReceipts()).find(r => r.material_status === 'Received Short' && r.job_numbers?.includes(jobId));
      if (!receipt) return;
      await updateMaterialReceipt(receipt.id, {
        material_status: 'Follow Ups',
        note_log: [...(receipt.note_log || []), { author: 'Ace', timestamp: noteStamp(), text: 'Proxy says the missing material is on the way. Merge it in when it arrives.', kind: 'system' }],
      });
    } catch { /* backend unavailable: the notification still tells Fulfillment */ }
  }

  function handleApprove() {
    if (!schema) return;
    for (const action of schema.outboundActions) {
      const draft = schema.drafts.find(d => d.key === action.key);
      const override = draftOverrides[action.key];
      const body = draft ? (override?.body ?? draft.body) : action.bodyPreview;
      const recipientEmail = draft ? (override?.to ?? '').trim() : action.recipientEmail;
      const ccEmails = draft ? (override?.cc ?? []) : action.ccEmails;
      const subject = draft ? (override?.subject ?? draft.subject ?? action.subject) : action.subject;
      const targets = action.jobIds ?? [job!.jobId];
      for (const jobId of targets) {
        appendFollowUp(jobId, {
          timestamp: proxyNowStamp(), author: 'ICT Associate', type: action.followUpType,
          recipientEmail, ccEmails, subject, bodyPreview: body, jobIds: targets, sourceEmailId: task.sourceEmailId, taskId: task.id,
          ticketKey: ticketKey(task, job!),
        });
      }
      // Only drafts with a visible email (not the silent MIC/internal log
      // entries) are real sent mail — record those in the shared Email
      // Inbox so Wayne (for fulfillment_escalation-sourced tasks like
      // UNMATCHED_MATERIAL_ROUTE) or Proxy (everything else, including
      // SHORTAGE_FOLLOWUP, which threads onto its own originating email)
      // sees what Ace actually sent.
      if (draft) {
        appendOutbound({
          refKey: targets.join(','), jobIds: targets,
          origin: task.source === 'fulfillment_escalation' ? 'fulfillment' : 'proxy',
          to: recipientEmail, cc: ccEmails, subject, body,
        });
      }
    }
    // What the person changed from Ace's draft, for Proxy Governance.
    const defaults = workflow.defaultEdits(task, job!);
    const changed = new Set<string>();
    schema.extractedFields.forEach(f => { if (f.editable && edits[f.key] !== defaults[f.key]) changed.add(f.label); });
    if (calcChanged) changed.add('Calculation');
    // Recipient/CC aren't counted: the person always fills those in.
    schema.drafts.forEach(d => {
      const o = draftOverrides[d.key];
      if (!o) return;
      if (o.subject !== undefined && o.subject !== d.subject) changed.add('Subject');
      if (o.body !== undefined && o.body !== d.body) changed.add('Email body');
    });
    recordApproval({
      taskId: task.id, workflowType: task.workflowType, jobId: job!.jobId, title: task.title,
      at: proxyNowStamp(), by: 'You', edits: [...changed],
      ticketKey: ticketKey(task, job!), closesTicket: !!schema.closesTicket,
    });
    if (schema.teamUpdate) {
      sendTeamMessage({ from: 'cary', to: 'wayne', ...schema.teamUpdate, jobId: job!.jobId, client: job!.issuer });
    }
    if (schema.awaitMaterial) markReceiptAwaitingMaterial(job!.jobId);
    const patch = workflow.onApprove?.({ task, job: job!, edits, email });
    // Approving is the decision the escalation was waiting for.
    const cleared = job!.escalatedToTeam ? { escalatedToTeam: null, escalatedToUserId: null, escalationReason: null } : {};
    if (patch || job!.escalatedToTeam) updateJob(job!.jobId, { ...patch, ...cleared });
    setApproved(true);
    setEditingFields(false);
    setCalcEditing(false);
    setDraftEditing({});
    updateSession(sessionId, { proxyTaskApproved: true });
    appendMsg(sessionId, {
      id: String(Date.now() + Math.random()),
      role: 'agent',
      content: `<span class="step-done" style="display:inline-block;background:#ECFDF3;color:#166534;font-size:10.5px;padding:1px 6px;border-radius:3px;margin-right:5px;font-weight:500">✓</span>${schema.approveConfirmation}`,
      time: now(),
      // Reviewable record of this card as approved — history stays a faithful
      // record even after job data or a later task for this job moves on.
      proxyTaskSnapshot: task,
      proxyTaskSnapshotApproved: true,
    });
  }

  function handleReject() {
    appendMsg(sessionId, {
      id: String(Date.now() + Math.random()),
      role: 'agent',
      content: 'Draft rejected. Adjust the extracted values, calculation, or drafted messages via Edit, then approve again.',
      time: now(),
    });
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-white">
      {/* header */}
      <div className="px-6 py-5 border-b border-[#E5E7EB] flex items-start gap-3.5">
        <span className="w-[34px] h-[34px] rounded-full bg-gradient-agent flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-lg leading-6 font-semibold text-[#043243]">{groupJobs.length > 1 ? `${task.title}` : `${job.issuer}, Job ${job.jobId}`}</div>
          {groupJobs.length > 1 && <div className="text-xs text-[#73747A] mt-0.5">{groupJobs.map(j => `${j.jobId} ${j.issuer}`).join(' · ')}</div>}
          <div className="text-[13px] text-[#595A60] mt-0.5">
            Ace picked this up from {SOURCE_INTRO[task.source]} and prepared {workflow.noun} for your review.
          </div>
          <div className="flex flex-wrap gap-2 mt-2.5">
            <Chip variant="type">{workflow.label}</Chip>
            <Chip variant="src">Source: {PROXY_SOURCE_LABEL[task.source]}</Chip>
            <Chip variant="amber">{band} · {job.daysToVoteCutoff}d to cutoff</Chip>
          </div>
        </div>
      </div>

      {!displayApproved && (job.escalatedToTeam || schema.escalation) && (
        <div data-tour="task-escalation" className="mx-6 mt-4 px-3.5 py-2.5 rounded-lg border border-[#DDD6FE] bg-[#F5F3FF] text-[13px] text-[#4C1D95]">
          <b className="font-semibold">{job.escalatedToTeam ? `Escalated to ${job.escalatedToTeam}.` : 'This one is the manager\'s call.'}</b>{' '}
          {job.escalatedToTeam ? (job.escalationReason ? `Comment: ${job.escalationReason}` : '') : schema.escalation}
        </div>
      )}

      <Stepper steps={workflow.steps} draftApproved={displayApproved} />

      <div className="px-6 flex-1">
        {/* 1. why — generic, every workflow */}
        <Section index={1} title="Why this is a task" tourId="why">
          <div className="bg-[#F6FAFB] border-l-[3px] border-[#1D9E75] rounded-md px-3 py-2.5 text-[13px] text-[#043243]">
            <b className="text-[#063F5A] font-semibold">Ace:</b> {task.reason}
          </div>
          <div className="mt-2.5 border border-[#E5E7EB] rounded-lg bg-white">
            <button onClick={() => setSrcOpen(o => !o)} className="w-full flex items-center gap-1.5 text-left px-3.5 py-2.5 text-[13px] font-semibold text-[#2563EB]">
              <span className="text-[#595A60]">{srcOpen ? '▾' : '▸'}</span>
              {email ? 'View source email' : `View ${task.sourceLabel || PROXY_SOURCE_LABEL[task.source].toLowerCase()}`}
            </button>
            {srcOpen && (
              <div className="border-t border-[#E5E7EB] p-0">
                {email ? <EmailCard email={email} /> : task.sourceDetail ? <DetailCard rows={task.sourceDetail.rows} /> : null}
              </div>
            )}
          </div>
          <div className="text-xs text-[#73747A] mt-2">
            {isHighConfidence
              ? 'Source collapsed by default.'
              : 'Source expanded. Please review it before approving.'}
          </div>
        </Section>

        {/* 2. extracted — schema-driven */}
        <Section index={2} title="What Ace extracted" tourId="extracted" trailing={!editingFields && <Chip variant="conf">auto-filled from the email + MIC</Chip>}>
          <div className="grid grid-cols-3 gap-px bg-[#E5E7EB] border border-[#E5E7EB] rounded-lg overflow-hidden">
            {schema.extractedFields.map(field => (
              <ExtractedFieldCell
                key={field.key} field={field} editing={editingFields}
                onChange={value => setEdits(prev => ({ ...prev, [field.key]: value }))}
              />
            ))}
          </div>
        </Section>

        {/* 3. calculation — schema-driven */}
        <Section index={3} title={schema.calculation.title} tourId="decision">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
            <div className="flex justify-between items-start gap-3 mb-2.5">
              <div className="text-[13px] text-[#595A60]">Rule applied: <b className="text-[#043243] font-semibold">{schema.calculation.ruleText}</b></div>
              {!readOnly && !displayApproved && (
                <button onClick={() => setCalcEditing(v => !v)} className="text-xs text-[#2563EB] font-medium flex-shrink-0">{calcEditing ? 'Done' : 'Edit'}</button>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              {schema.calculation.rows.map(row => <CalcRowView key={row.label} row={row} value={calcValue(row)} editing={calcEditing} onChange={setCalcValue(row)} />)}
              <CalcRowView row={schema.calculation.totalRow} value={calcValue(schema.calculation.totalRow)} emphasize editing={calcEditing} onChange={setCalcValue(schema.calculation.totalRow)} />
              {schema.calculation.deltaRow && <CalcRowView row={schema.calculation.deltaRow} value={calcValue(schema.calculation.deltaRow)} editing={calcEditing} onChange={setCalcValue(schema.calculation.deltaRow)} />}
            </div>
          </div>
          {calcChanged && <div className="text-xs text-[#73747A] mt-2">You changed Ace's numbers. Check the email below says the same thing.</div>}
        </Section>

        {/* 4. drafts — schema-driven */}
        <Section index={4} title="Ace drafted the outbound actions" tourId="drafts">
          {schema.drafts.length === 0 && <div className="text-[13px] text-[#595A60]">No email needed for this one. See what Ace will document below.</div>}
          {schema.drafts.map(draft => (
            <DraftCard
              key={draft.key} icon={draft.icon} label={draft.label} note={draft.note}
              to={draftOverrides[draft.key]?.to ?? ''}
              cc={draftOverrides[draft.key]?.cc ?? []}
              subject={draftOverrides[draft.key]?.subject ?? draft.subject}
              body={draftOverrides[draft.key]?.body ?? draft.body}
              editing={!!draftEditing[draft.key]}
              onToggleEdit={() => setDraftEditing(prev => ({ ...prev, [draft.key]: !prev[draft.key] }))}
              onChange={patch => setDraftOverrides(prev => ({ ...prev, [draft.key]: { ...prev[draft.key], ...patch } }))}
            />
          ))}
        </Section>

        {/* 5. writeback — schema-driven */}
        <Section index={5} title="On approve, Ace will" tourId="approve-plan">
          <div className="text-[13px] text-[#595A60] leading-relaxed">
            {schema.approveSummary.map((item, i) => (
              <React.Fragment key={item}>
                {i > 0 && <>&nbsp;·&nbsp;</>}✓ {item}
              </React.Fragment>
            ))}
          </div>
        </Section>

        {/* UAT feedback on Ace's work for this task */}
        {!readOnly && (
          <div className="mb-5 px-4 py-3 rounded-lg border border-trim bg-wash">
            <QuickCheck
              question="Did Ace get this one right?"
              choices={[{ label: 'Yes', good: true }, { label: 'Not quite' }]}
              reasons={['Wrong details', 'Wrong decision', 'Draft needs work', "Not how we'd handle it"]}
              prompt="What would you have done differently?"
              page="Ace review card"
              about={`${workflow.label}, job ${job.jobId}`}
              source="ace-check"
            />
          </div>
        )}
      </div>

      {/* footer — generic */}
      <div data-tour="task-actions" className="sticky bottom-0 bg-white border-t border-[#E5E7EB] px-6 py-4 flex items-center gap-3">
        {readOnly ? (
          <div className="text-[13px] font-semibold text-[#595A60]">
            {displayApproved ? "Approved at the time. You're viewing history, so it's read only." : "Superseded before anyone acted on it. You're viewing history, so it's read only."}
          </div>
        ) : approved ? (
          <div className="flex items-center gap-2 text-[13px] font-semibold text-[#0F6E56]">
            <svg width="16" height="16" viewBox="0 0 14 14" fill="none"><path d="M2 7l3.5 3.5L12 3" stroke="#0F6E56" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            Approved. Sent, documented, and moved to Done.
          </div>
        ) : (
          <>
            <button data-tour="task-approve" onClick={handleApprove} disabled={missingRecipient} className="text-sm font-semibold text-white rounded-lg px-[18px] py-2.5 disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: GRAD }}>
              Approve &amp; send
            </button>
            {canEditFields && (
              <button onClick={() => setEditingFields(v => !v)} className="text-sm font-semibold rounded-lg px-[18px] py-2.5 border border-[#e5e5e5] text-[#043243] bg-white">
                {editingFields ? 'Done editing' : 'Edit'}
              </button>
            )}
            <button onClick={handleReject} className="text-sm font-semibold text-[#595A60] px-[18px] py-2.5">
              Reject
            </button>
            {job.escalatedToTeam ? (
              <span className="text-xs text-[#73747A] whitespace-nowrap">With {job.escalatedToTeam}</span>
            ) : (
              <EscalateControl
                label="Escalate to manager"
                buttonClass="text-sm font-semibold text-[#595A60] px-2 py-2.5 whitespace-nowrap"
                onEscalate={reason => escalate(job.jobId, reason)}
              />
            )}
            <span className={`ml-auto text-xs max-w-[220px] text-right ${missingRecipient ? 'text-[#7a5b00]' : 'text-[#73747A]'}`}>
              {missingRecipient ? 'Add a recipient to each email first.' : "Ace handled extraction + calc. You're validating before anything goes out."}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
